import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack, FormHelperText,
    Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Home, InsertPhoto } from '@mui/icons-material';
import productApi from '../../../../services/productApi';
import categoriesApi from '../../../../services/categories';
import ImageUploader from '../../common/ImageUploader';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import { getImageUrl } from '../../../../services/api';

const ProductBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        categoryId: '',
        prodName: '',
        altText: '',
        description:'',
        isActive: true,
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [errors, setErrors] = useState({ productId: '',fullName:'', email: '',contactNo:'', remarks: '' });
    const [touched, setTouched] = useState({ productId: false, fullName: false,email:false,
        contactNo:false,remarks: false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await categoriesApi.getAll();
                setCategories(response);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    useEffect(() => {
        if (!isNew) {
            const fetchProduct= async () => {
                try {
                    const product = await productApi.getById(id);
                    setFormData({
                        categoryId: product.categoryId || null,
                        prodName: product.prodName || '',
                        description: product.description || '',
                        altText: product.altText || '',
                        isActive: product.isActive
                    });

                    if (product.imageUrl) {
                        const fullUrl = getImageUrl(product.imageUrl);
                        setPreviewUrl(fullUrl);
                        setExistingImageUrl(fullUrl);
                    }
                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load product', 'error');
                    navigate('/admin/products');
                } finally {
                    setLoading(false);
                }
            };
            fetchProduct();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'categoryId': return value ? '' : 'Category is required';
            case 'prodName': return value.trim() ? '' : 'Name is required';
            case 'altText': return value.trim() ? '' : 'Alt text is required';
            case 'description': return value.trim() ? '' : 'Description is required';
            case 'image':
                return (isNew ? !previewUrl : (!previewUrl && !existingImageUrl))
                    ? 'Image is required'
                    : '';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleImageSelect = (file) => {
        if (!file) return;

        if (previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, image: file }));
        setPreviewUrl(objectUrl);
        setErrors(prev => ({ ...prev, image: '' }));
    };

    const handleSubmit = async () => {
        setTouched({ categoryId: true,prodName:true, altText: true,description: true, image: true });
        if (!formData.categoryId || !formData.prodName.trim()) {
            setErrors({
                categoryId: !formData.categoryId ? 'Category is required' : '',
                prodName: !formData.prodName.trim() ? 'Name is required' : '',
                description: !formData.description.trim() ? 'Description is required' : '',
                altText: !formData.altText.trim() ? 'Alt text is required' : '',
                image: isNew && !previewUrl ? 'Image is required' : ''
            });
            return;
        }

        if (isNew && !previewUrl) {
            setErrors(prev => ({ ...prev, image: 'Image is required' }));
            return;
        }

        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this product?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });

        if (!confirm.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('categoryId', formData.categoryId);
            formDataToSend.append('prodName', formData.prodName || '');
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('altText', formData.altText);
            formDataToSend.append('isActive', formData.isActive);

            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }
            for (let [key, value] of formDataToSend.entries()) {
                console.log(key, value);
            }

            if (isNew) {
                await productApi.create(formDataToSend);
            } else {
                await productApi.update(id, formDataToSend);
            }

            Swal.fire('Success', `Product ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/products');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/products')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="/admin" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Product Catalog
                            </Link>
                            <Link href="/admin/products" underline="hover" color="inherit">
                                Products
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Product Information</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleChange}
                                    label="Category"
                                >
                                    {categories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.catName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                required
                                fullWidth
                                label="Name"
                                name="prodName"
                                value={formData.prodName}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.prodName && Boolean(errors.prodName)}
                                helperText={touched.prodName && errors.prodName}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                required
                                fullWidth
                                label="Alt Text"
                                name="altText"
                                value={formData.altText}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.altText && Boolean(errors.altText)}
                                helperText={touched.altText && errors.altText}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) =>
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Image Upload</Typography>

                            <Box sx={{
                                p: 2,
                                mb: 3,
                                border: '1px solid',
                                borderColor: errors.image && touched.image ? 'error.main' : 'divider',
                                borderRadius: 1
                            }}>
                                <ImageUploader
                                    onFileSelect={handleImageSelect}
                                    accept="image/*"
                                    maxSize={5 * 1024 * 1024}
                                />
                                {errors.image && touched.image && (
                                    <FormHelperText error>{errors.image}</FormHelperText>
                                )}
                                <Typography variant="caption" color="textSecondary">
                                    JPEG, PNG, GIF. Max 5MB.
                                </Typography>
                            </Box>

                            <Typography variant="h5" gutterBottom>Preview</Typography>

                            <Box sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                minHeight: 200,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                {previewUrl || existingImageUrl ? (
                                    <Box sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        maxWidth: 300,
                                        maxHeight: 200
                                    }}>
                                        <img
                                            src={previewUrl || existingImageUrl}
                                            alt="Product Preview"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                borderRadius: 4
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                opacity: 0,
                                                transition: 'opacity 0.3s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 1 }
                                            }}
                                            onClick={() => window.open(previewUrl || existingImageUrl, '_blank')}
                                        >
                                            <Typography variant="caption" color="white">
                                                View
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box textAlign="center">
                                        <InsertPhoto color="disabled" sx={{ fontSize: 48 }} />
                                        <Typography>No image selected</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/products')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductBody;